<script setup lang="ts">
import { computed, ref } from 'vue';
import Column from 'primevue/column';
import { KeyRound, MailPlus, Pencil, Plus, Search, ShieldOff, UserCheck, UserX } from '@lucide/vue';
import type { FiltroDuefa, FiltroStatoUtente, PaginationQuery, Utente } from '@evolfin/shared';
import { useAuthStore } from '@/stores/auth';
import { useAvvisi } from '@/composables/useAvvisi';
import { useInvalidation } from '@/composables/useInvalidation';
import { useConferma } from '@/composables/useConferma';
import { useListaQuery } from '@/composables/useListaQuery';
import { useQuery } from '@tanstack/vue-query';
import { EvolfinApiError } from '@/api/client';
import TabellaDati from '@/components/ui/TabellaDati.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import PulsanteEsporta from '@/features/export/components/PulsanteEsporta.vue';
import AvatarUtente from '@/components/ui/AvatarUtente.vue';
import { formattaData, formattaDataOra } from '@/utils/formato';
import FormUtenteDialog from '../components/FormUtenteDialog.vue';
import NavUtenti from '../components/NavUtenti.vue';
import {
  disattivaUtente,
  listaRuoli,
  listaUtenti,
  reinvitaUtente,
  resetDuefaUtente,
  riattivaUtente,
  type QueryUtenti,
} from '../api';

definePage({ meta: { titolo: 'Utenti' } });

const auth = useAuthStore();
const avvisi = useAvvisi();
const invalidate = useInvalidation('utenti');
const conferma = useConferma();

const puoCreare = computed(() => auth.ha('can_create_utenti'));
const puoModificare = computed(() => auth.ha('can_write_utenti'));
const puoDisattivare = computed(() => auth.ha('can_delete_utenti'));

// I ruoli servono al form e al filtro; caricati una volta e riusati (pochi record).
const { data: ruoli } = useQuery({ queryKey: ['ruoli'], queryFn: listaRuoli });

const lista = useListaQuery<Utente>('utenti', (q) => listaUtenti(q as QueryUtenti), {
  iniziale: {
    sort: 'cognome',
    stato: 'attivi',
    duefa: 'tutti',
  } as unknown as Partial<PaginationQuery>,
});
const { righe, totale, query, caricamento, onPage, onSort, cerca } = lista;

const testoRicerca = ref('');

// Filtri: computed sulla stessa query reattiva (fanno parte della queryKey → refetch).
function filtro<K extends keyof QueryUtenti>(chiave: K, def: QueryUtenti[K]) {
  return computed({
    get: () => ((query as QueryUtenti)[chiave] ?? def) as QueryUtenti[K],
    set: (v: QueryUtenti[K]) => {
      (query as QueryUtenti)[chiave] = v;
      query.page = 1;
    },
  });
}
const statoFiltro = filtro('stato', 'attivi' as FiltroStatoUtente);
const duefaFiltro = filtro('duefa', 'tutti' as FiltroDuefa);
const ruoloFiltro = computed<string>({
  get: () => {
    const id = (query as QueryUtenti).ruoloId;
    return id == null ? '' : String(id);
  },
  set: (v) => {
    (query as QueryUtenti).ruoloId = v ? Number(v) : undefined;
    query.page = 1;
  },
});

// Dialog di creazione/modifica.
const dialogAperto = ref(false);
const utenteInModifica = ref<Utente | null>(null);

function apriCreazione(): void {
  utenteInModifica.value = null;
  dialogAperto.value = true;
}
function apriModifica(u: Utente): void {
  utenteInModifica.value = u;
  dialogAperto.value = true;
}

async function conAvviso(azione: () => Promise<void>, ok: string): Promise<void> {
  try {
    await azione();
    avvisi.successo(ok);
    await invalidate();
  } catch (errore) {
    const messaggio =
      errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
    avvisi.errore('Errore', messaggio);
  }
}

async function disattiva(u: Utente): Promise<void> {
  if (
    await conferma({
      titolo: 'Disattiva utente',
      messaggio: `Disattivare ${u.nome} ${u.cognome}? Non potrà più accedere; lo storico resta.`,
      etichettaConferma: 'Disattiva',
      distruttiva: true,
    })
  ) {
    await conAvviso(() => disattivaUtente(u.id), 'Utente disattivato');
  }
}

async function reinvita(u: Utente): Promise<void> {
  if (
    await conferma({
      titolo: 'Reinvia invito',
      messaggio: `Inviare un nuovo link di attivazione a ${u.email}?`,
      etichettaConferma: 'Reinvia',
    })
  ) {
    await conAvviso(() => reinvitaUtente(u.id), 'Invito reinviato');
  }
}

async function resetDuefa(u: Utente): Promise<void> {
  if (
    await conferma({
      titolo: 'Reimposta 2FA',
      messaggio: `Disattivare la verifica in due passaggi di ${u.nome} ${u.cognome}? Potrà riattivarla dal proprio account.`,
      etichettaConferma: 'Reimposta',
      distruttiva: true,
    })
  ) {
    await conAvviso(() => resetDuefaUtente(u.id), '2FA reimpostata');
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <header class="flex flex-col items-stretch gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">Impostazioni</p>
        <h1 class="mt-1 font-display text-[33px] leading-[1.07] font-semibold">Utenti</h1>
        <p class="mt-1 hidden text-[13px] text-ink-secondary sm:block">
          Lo staff Evolfin: ruoli, privilegi e attivazione degli account.
        </p>
      </div>
      <PulsanteAzione v-if="puoCreare" class="w-full sm:w-auto" @click="apriCreazione">
        Nuovo utente
        <template #icona><Plus :size="16" :stroke-width="2" /></template>
      </PulsanteAzione>
    </header>

    <NavUtenti />

    <div class="card p-0">
      <!-- Barra strumenti: ricerca + filtri (stato, 2FA, ruolo). -->
      <div class="flex flex-wrap items-center gap-3 border-b border-line-soft px-4 py-3">
        <label class="relative min-w-[14rem] flex-1">
          <Search
            class="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-muted"
            :stroke-width="2"
            aria-hidden="true"
          />
          <input
            v-model="testoRicerca"
            type="search"
            placeholder="Cerca per nome o email…"
            class="w-full rounded-sm border border-line bg-surface py-2 pr-3 pl-9 text-[13px] text-ink transition-[border-color] focus:border-[var(--color-accent)]"
            @input="cerca(testoRicerca)"
          />
        </label>

        <select v-model="statoFiltro" class="filtro" aria-label="Filtra per stato">
          <option value="attivi">Attivi</option>
          <option value="disattivati">Disattivati</option>
          <option value="tutti">Tutti gli stati</option>
        </select>

        <select v-model="duefaFiltro" class="filtro" aria-label="Filtra per 2FA">
          <option value="tutti">2FA: tutte</option>
          <option value="attiva">2FA attiva</option>
          <option value="disattiva">2FA non attiva</option>
        </select>

        <select v-model="ruoloFiltro" class="filtro" aria-label="Filtra per ruolo">
          <option value="">Tutti i ruoli</option>
          <option v-for="r in ruoli ?? []" :key="r.id" :value="String(r.id)">{{ r.nome }}</option>
        </select>

        <PulsanteEsporta
          dataset="utenti"
          endpoint="/api/utenti/export"
          :query="query"
          :totale="totale"
          nome-file="utenti.csv"
        />
      </div>

      <TabellaDati
        :righe="righe"
        :totale="totale"
        :query="query"
        :caricamento="caricamento"
        @page="onPage"
        @sort="onSort"
      >
        <Column field="cognome" header="Utente" :sortable="true" primaria>
          <template #body="{ data }: { data: Utente }">
            <div class="flex items-center gap-2.5" :class="data.disattivatoIl && 'opacity-55'">
              <AvatarUtente
                :id="data.id"
                :nome="data.nome"
                :cognome="data.cognome"
                :email="data.email"
                :ha-immagine="data.haImmagine"
                :dimensione="32"
              />
              <div class="min-w-0">
                <p class="truncate font-medium text-ink">{{ data.nome }} {{ data.cognome }}</p>
                <p class="truncate text-[12px] text-ink-muted">{{ data.email }}</p>
              </div>
            </div>
          </template>
        </Column>

        <Column field="ruolo" header="Ruolo" :sortable="true">
          <template #body="{ data }: { data: Utente }">
            <span class="text-[13px] text-ink-secondary">{{ data.ruolo }}</span>
            <span
              v-if="data.privilegi.length"
              class="ml-1.5 rounded-pill bg-[var(--surface-accent-soft)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--color-primary)]"
              :title="`${data.privilegi.length} privilegi extra`"
            >
              +{{ data.privilegi.length }}
            </span>
          </template>
        </Column>

        <Column header="2FA">
          <template #body="{ data }: { data: Utente }">
            <span
              v-if="data.duefaAttiva"
              class="inline-flex items-center gap-1 rounded-pill bg-[var(--surface-accent-soft)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-primary)]"
            >
              <KeyRound :size="12" :stroke-width="2" /> Attiva
            </span>
            <span v-else class="text-[12px] text-ink-muted">—</span>
          </template>
        </Column>

        <Column field="ultimoAccessoIl" header="Ultimo accesso" :sortable="true">
          <template #body="{ data }: { data: Utente }">
            <span v-if="data.ultimoAccessoIl" class="text-[12.5px] text-ink-secondary tabular-nums">
              {{ formattaDataOra(data.ultimoAccessoIl) }}
            </span>
            <span v-else class="text-[12px] text-ink-muted">Mai</span>
          </template>
        </Column>

        <Column field="creatoIl" header="Creato il" :sortable="true">
          <template #body="{ data }: { data: Utente }">
            <span class="text-[12.5px] text-ink-secondary tabular-nums">
              {{ formattaData(data.creatoIl) }}
            </span>
          </template>
        </Column>

        <Column header="Stato">
          <template #body="{ data }: { data: Utente }">
            <span
              v-if="data.disattivatoIl"
              class="rounded-pill bg-line-soft px-2 py-0.5 text-[11px] font-medium text-ink-muted"
            >
              Disattivato
            </span>
            <span
              v-else
              class="rounded-pill px-2 py-0.5 text-[11px] font-medium"
              style="
                background: color-mix(in oklch, var(--color-success) 12%, transparent);
                color: var(--color-success);
              "
            >
              Attivo
            </span>
          </template>
        </Column>

        <Column header="" azioni :style="{ width: '9rem' }">
          <template #body="{ data }: { data: Utente }">
            <div class="flex items-center justify-end gap-1">
              <button
                v-if="puoModificare"
                type="button"
                class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink"
                title="Modifica"
                @click="apriModifica(data)"
              >
                <Pencil :size="16" :stroke-width="1.8" />
              </button>
              <button
                v-if="puoModificare && !data.disattivatoIl"
                type="button"
                class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink"
                title="Reinvia invito"
                @click="reinvita(data)"
              >
                <MailPlus :size="16" :stroke-width="1.8" />
              </button>
              <button
                v-if="puoModificare && data.duefaAttiva"
                type="button"
                class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink"
                title="Reimposta 2FA"
                @click="resetDuefa(data)"
              >
                <ShieldOff :size="16" :stroke-width="1.8" />
              </button>
              <button
                v-if="puoDisattivare && !data.disattivatoIl"
                type="button"
                class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-[var(--color-danger-text)]"
                title="Disattiva"
                @click="disattiva(data)"
              >
                <UserX :size="16" :stroke-width="1.8" />
              </button>
              <button
                v-if="puoDisattivare && data.disattivatoIl"
                type="button"
                class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-[var(--color-primary)]"
                title="Riattiva"
                @click="conAvviso(() => riattivaUtente(data.id), 'Utente riattivato')"
              >
                <UserCheck :size="16" :stroke-width="1.8" />
              </button>
            </div>
          </template>
        </Column>

        <template #vuoto>
          <p class="py-10 text-center text-[13px] text-ink-secondary">Nessun utente trovato.</p>
        </template>
      </TabellaDati>
    </div>

    <FormUtenteDialog
      v-model="dialogAperto"
      :utente="utenteInModifica"
      :ruoli="ruoli ?? []"
      @salvato="invalidate()"
    />
  </div>
</template>

<style scoped>
.filtro {
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  /* `background-color`, non la shorthand `background`: quest'ultima azzererebbe
     il `background-image` (la chevron globale sui `<select>`, vedi main.css). */
  background-color: var(--color-surface);
  /* Solo padding sinistro/verticale: quello destro lo riserva la regola base sui
     `<select>` (main.css) per la freccia. Niente `padding` shorthand qui. */
  padding-block: 0.5rem;
  padding-inline-start: 0.65rem;
  font-size: 13px;
  color: var(--color-text);
  transition: border-color 120ms ease;
}
.filtro:focus {
  outline: none;
  border-color: var(--color-accent);
}
</style>
